import React from "react";

import { Box, Typography } from "@material-ui/core";
import styled from "styled-components/macro";

import { colors } from "constants/theme";

import { normalizePicOrAbn } from "selectors/sideLoadedBusinesses";

const MAX_PICS_SHOWN = 5;

const MatchingPic = styled.span`
  background: ${({ theme }) => theme.colors.successGreen};
  color: ${({ theme }) => theme.colors.black};
  border-radius: ${({ theme }) => theme.radii[1]}px;
  padding: 0 ${({ theme }) => theme.space[0] + 3}px;
`;

// PICs the temporary Business shares come first, highlighted.
function renderPics(pics: string[], matchingPics: string[]): React.ReactNode {
  const isMatching = (pic: string) =>
    matchingPics.includes(normalizePicOrAbn(pic));
  const sorted = [
    ...pics.filter(isMatching),
    ...pics.filter(pic => !isMatching(pic)),
  ];
  const shown = sorted.slice(0, MAX_PICS_SHOWN).map((pic, index) => (
    <React.Fragment key={pic}>
      {index > 0 && ", "}
      {isMatching(pic) ? <MatchingPic>{pic}</MatchingPic> : pic}
    </React.Fragment>
  ));
  return sorted.length > MAX_PICS_SHOWN ? <>{shown}, ...</> : shown;
}

// data-section-heading lets the listbox undim this disabled option.
const SectionHeading = styled.div`
  width: 100%;
  padding-top: ${({ theme }) => theme.space[1]}px;
  border-top: 1px solid ${colors.gray78};

  li:first-of-type > & {
    padding-top: 0;
    border-top: none;
  }
`;

const SubsectionHeading = styled.div`
  width: 100%;
  padding-left: ${({ theme }) => theme.space[1]}px;
  font-style: italic;
`;

// Follows the row's text colour once highlighted, so it stays readable on
// the blue.
const Details = styled(Typography)`
  color: ${colors.gray40};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;

  li[data-focus="true"] &,
  li[aria-selected="true"] & {
    color: inherit;
    opacity: 0.85;
  }
`;

interface MatchSearchOptionProps {
  option: {
    id?: string;
    label: string;
    abn?: string | null;
    primaryContactEmail?: string | null;
    properties?: { PIC: string }[];
    isSectionHeading?: boolean;
    isSubsectionHeading?: boolean;
    subsection?: string;
  };
  // The temporary Business's PICs, normalised.
  matchingPics: string[];
}

// Two lines per option: the name, then its details.
export function MatchSearchOption({
  option,
  matchingPics,
}: MatchSearchOptionProps) {
  if (option.isSectionHeading) {
    return (
      <SectionHeading data-section-heading>
        <Typography variant="subtitle2" style={{ color: colors.gray40 }}>
          {option.label}
        </Typography>
      </SectionHeading>
    );
  }
  if (option.isSubsectionHeading) {
    return (
      <SubsectionHeading data-subsection-heading>
        <Typography variant="caption" style={{ color: colors.gray40 }}>
          {option.label}
        </Typography>
      </SubsectionHeading>
    );
  }
  // Status rows, e.g. "No results".
  if (!option.id) {
    return <Typography color="textSecondary">{option.label}</Typography>;
  }

  const pics = (option.properties ?? [])
    .map(property => property.PIC)
    .filter(Boolean);
  const details = [
    option.abn && `ABN ${option.abn}`,
    pics.length > 0 && renderPics(pics, matchingPics),
    option.primaryContactEmail,
  ]
    .filter(Boolean)
    .map((detail, index) => (
      <React.Fragment key={index}>
        {index > 0 && " · "}
        {detail}
      </React.Fragment>
    ));

  return (
    // Options grouped under a subheading (eg a data set) sit indented below it.
    <Box width="100%" minWidth={0} paddingLeft={option.subsection ? 2 : 0}>
      <Typography noWrap style={{ fontWeight: 600 }}>
        {option.label}
      </Typography>
      {details.length > 0 && <Details variant="body2">{details}</Details>}
    </Box>
  );
}
